import { useQuery } from '@apollo/client';
import { ShoppingBagOutlined, TryOutlined } from '@mui/icons-material';
import { appBarClasses, Box, CircularProgress, Stack, ToggleButton } from '@mui/material';
import { Permission } from '@procureai/permissions';
import { Restricted, usePermissions } from '@procureai/permissions-ui';
import { useButtonMenu, WidgetIcon } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SvbrgConversationEntityVariant, TlscpEntityVariant, TsrctCommentParentVariant } from '@/generated/gql/graphql';
import { CommentsPopoverButton } from '@/legacy/components/CommentsPopoverButton/CommentsPopoverButton';
import { ModuleEntityType } from '~/common/types';
import { DemandProcessStatusChip } from '~/components/DemandProcessStatusChip/DemandProcessStatusChip';
import { DemandProcessStepsButton } from '~/components/DemandProcessStepsButton/DemandProcessStepsButton';
import { ModuleContextProvider, ModuleContextProviderValue } from '~/components/ModuleContextProvider';
import { ModulePage } from '~/components/ModulePage/ModulePage';
import { useModulePageConfig } from '~/components/ModulePageConfigProvider';
import { ModulePageHeader } from '~/components/ModulePageHeader/ModulePageHeader';
import { ModuleSlot } from '~/components/ModuleSlot/ModuleSlot';
import { PageHeaderActionMenuButton } from '~/components/PageHeaderActionMenuButton/PageHeaderActionMenuButton';
import { useOpenClose } from '~/hooks';
import { useProcessStepsAutoRefresh } from '~/hooks/useProcessStepsAutoRefresh/useProcessStepsAutoRefresh';
import { ProcessStepsAutoRefreshVariant } from '~/hooks/useProcessStepsAutoRefresh/useProcessStepsAutoRefresh.util';
import { ModuleSlotId } from '~/modules';
import { BusinessLogPopoverButton } from '~/modules/BusinessLogWidget/components/BusinessLogPopoverButton/BusinessLogPopoverButton';
import { useDemandDetail, useTabId } from '~/modules/Module.hooks';
import { RouteId } from '~/routes/RouteId';

import { ActionsMenu } from './components';
import { DemandConversationDrawer } from './components/DemandConversationDrawer/DemandConversationDrawer';
import { DemandPriority } from './components/DemandPriority';
import { DEMAND_CONVERSATION_SETTINGS_QUERY, NEGOTIATION_DEMAND_DETAIL_QUERY } from './NegotiationDemandDetail.gql';

export const NegotiationDemandDetail = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'NegotiationDemandDetail' });
  const { demandId } = useDemandDetail();
  const { tabId } = useTabId();
  const { menuProps, buttonProps } = useButtonMenu();
  const { modules, tabs } = useModulePageConfig({ pageId: RouteId.negotiationDemandDetail(), tabId });
  const { hasPermission } = usePermissions();

  const { data, loading: queryLoading } = useQuery(NEGOTIATION_DEMAND_DETAIL_QUERY, {
    variables: {
      id: demandId,
    },
  });

  const { sequenceNumber, status, processStatus, priority } = data?.tesseract.demand || {};

  const contextValue = useMemo<ModuleContextProviderValue>(
    () => ({ entity: ModuleEntityType.DEMAND, entityId: demandId }),
    [demandId],
  );

  useProcessStepsAutoRefresh({
    variant: ProcessStepsAutoRefreshVariant.DEMAND_DETAIL,
    id: demandId,
  });

  const { data: defaultConversationSettings, loading: defaultConversationSettingsLoading } = useQuery(
    DEMAND_CONVERSATION_SETTINGS_QUERY,
    {
      variables: {
        entityId: demandId,
        entityVariant: SvbrgConversationEntityVariant.DEMAND,
      },
      // Without permission for demand conversations, the drawer won't be accessible anyway
      skip: !hasPermission(Permission.ConversationDemandUse),
    },
  );

  const chatDisabled =
    defaultConversationSettingsLoading || !defaultConversationSettings?.sevenbridges.conversationSettings?.id;

  const [openCloseProps, handleOpen] = useOpenClose();
  const { open, onClose } = openCloseProps;
  return (
    <>
      <ModulePage
        header={
          <ModulePageHeader
            // Need to explicitly set zIndex for header to 1201 to overlap drawer
            // TODO: Should be done in ui-library already but requires changes for Intake's drawer
            sx={(theme) => ({
              [`& .${appBarClasses.root}`]: { zIndex: theme.zIndex.drawer + 1 },
            })}
            title={t('title', { sequenceNumber })}
            left={
              queryLoading ? (
                <CircularProgress size={20} />
              ) : (
                <Box sx={{ marginLeft: 2 }}>
                  <DemandProcessStatusChip processStatus={processStatus} status={status} />
                </Box>
              )
            }
            icon={<WidgetIcon icon={<ShoppingBagOutlined fontSize="small" />} />}
            tabs={tabs}
            right={
              <Stack direction="row" spacing={3} sx={{ alignItems: 'center' }}>
                <DemandPriority id={demandId} priority={priority} />
                <DemandProcessStepsButton steps={data?.tesseract.demand.processSteps} demandId={demandId} />
                <CommentsPopoverButton entityId={demandId} entityVariant={TsrctCommentParentVariant.DEMAND} />
                <BusinessLogPopoverButton
                  entityId={demandId}
                  entityVariant={TlscpEntityVariant.DEMAND}
                ></BusinessLogPopoverButton>
                <PageHeaderActionMenuButton
                  needs={{
                    permission: {
                      any: [
                        Permission.DemandReset,
                        Permission.DemandCancel,
                        Permission.DemandDelete,
                        Permission.ConversationDemandManage,
                      ],
                    },
                  }}
                  intercomTarget="NegotiationDemandDetails/Actions"
                  {...buttonProps}
                >
                  {status && (
                    <ActionsMenu
                      id={demandId}
                      permissions={data?.tesseract.demand.permissions}
                      status={status}
                      {...menuProps}
                    />
                  )}
                </PageHeaderActionMenuButton>
                <Restricted
                  needs={{
                    permission: Permission.ConversationDemandUse,
                  }}
                >
                  {/* Standalone ToggleButton still requires value, even though it isn't used */}
                  <ToggleButton
                    value="chat"
                    selected={open}
                    onClick={open ? onClose : handleOpen}
                    disabled={chatDisabled}
                  >
                    <TryOutlined sx={{ mr: 2 }} />
                    {t('action.chat.label')}
                  </ToggleButton>
                </Restricted>
              </Stack>
            }
          />
        }
        modules={
          <ModuleContextProvider value={contextValue}>
            <ModuleSlot id={ModuleSlotId.MAIN} modules={modules} />
          </ModuleContextProvider>
        }
        drawer={
          sequenceNumber ? (
            <Restricted needs={{ permission: Permission.ConversationDemandUse }}>
              <DemandConversationDrawer sequenceNumber={sequenceNumber} hasTabs={!!tabs} {...openCloseProps} />
            </Restricted>
          ) : null
        }
      ></ModulePage>
    </>
  );
};
